import React from "react";
import { Activity, Plus, Trash2, Edit2 } from "lucide-react";

const AllowancesList = ({
  t,
  currentTheme,
  formStyles,
  isRTL,
  loading,
  allowances,
  onDeleteAllowance,
  onEditAllowance,
}) => {
  return (
    <>
      {/* Allowances List */}
      <div style={formStyles.section}>
        <h3
          style={{
            margin: "0 0 20px 0",
            color: currentTheme.colors.textPrimary,
            fontSize: "20px",
            fontWeight: "700",
          }}
        >
          {t("currentAllowances") || "Current Allowances"} ({allowances.length})
        </h3>

        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: currentTheme.colors.textSecondary,
            }}
          >
            <Activity
              size={32}
              style={{ animation: "spin 1s linear infinite" }}
            />
            <p>{t("loading")}</p>
          </div>
        ) : allowances.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: currentTheme.colors.textSecondary,
            }}
          >
            <Plus size={48} style={{ opacity: 0.3, marginBottom: "16px" }} />
            <p>{t("noAllowances") || "No allowances added"}</p>
          </div>
        ) : (
          allowances.map((allowance, index) => (
            <div
              key={allowance._id || allowance.id || index}
              style={formStyles.allowanceCard}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = currentTheme.shadows.card;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        background: `${currentTheme.colors.success}20`,
                        color: currentTheme.colors.success,
                        padding: "6px 12px",
                        borderRadius: "8px",
                        fontSize: "12px",
                        fontWeight: "700",
                        textTransform: "uppercase",
                      }}
                    >
                      {t(allowance.type)}
                    </span>

                    {allowance.days ? (
                      <span
                        style={{
                          fontSize: "13px",
                          color: currentTheme.colors.textMuted,
                        }}
                      >
                        {allowance.days} {t("days")}
                      </span>
                    ) : null}

                    {allowance.hours ? (
                      <span
                        style={{
                          fontSize: "13px",
                          color: currentTheme.colors.textMuted,
                        }}
                      >
                        {allowance.hours} {t("hours") || "hrs"}
                      </span>
                    ) : null}

                    {allowance.percentage ? (
                      <span
                        style={{
                          fontSize: "13px",
                          color: currentTheme.colors.textMuted,
                        }}
                      >
                        {allowance.percentage}%
                      </span>
                    ) : null}

                    {allowance.isRecurring ? (
                      <span
                        style={{
                          background: `${currentTheme.colors.info}20`,
                          color: currentTheme.colors.info,
                          padding: "3px 8px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "700",
                        }}
                      >
                        {t("recurring") || "Recurring"}
                      </span>
                    ) : null}

                    {allowance.isTaxable === false ? (
                      <span
                        style={{
                          background: `${currentTheme.colors.warning}20`,
                          color: currentTheme.colors.warning,
                          padding: "3px 8px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "700",
                        }}
                      >
                        {t("nonTaxable") || "Non-taxable"}
                      </span>
                    ) : null}
                  </div>

                  <div
                    style={{
                      fontSize: "15px",
                      color: currentTheme.colors.textPrimary,
                      marginBottom: "8px",
                      fontWeight: "600",
                    }}
                  >
                    {allowance.description || t("noDescription")}
                  </div>

                  <div
                    style={{
                      fontSize: "13px",
                      color: currentTheme.colors.textMuted,
                    }}
                  >
                    {t("date")}:{" "}
                    {allowance.attendance?.date
                      ? new Date(allowance.attendance.date).toLocaleDateString(
                          isRTL ? "ar-EG" : "en-GB"
                        )
                      : allowance.created_at
                      ? new Date(allowance.created_at).toLocaleDateString(
                          isRTL ? "ar-EG" : "en-GB"
                        )
                      : "N/A"}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "24px",
                      fontWeight: "800",
                      color: currentTheme.colors.success,
                      fontFamily: "monospace",
                    }}
                  >
                    +{Number(allowance.amount || 0).toLocaleString()}{" "}
                    {t("currency")}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onEditAllowance) onEditAllowance(allowance);
                      }}
                      style={{
                        background: "transparent",
                        border: `1px solid ${currentTheme.colors.border}`,
                        cursor: "pointer",
                        color: currentTheme.colors.textSecondary,
                        padding: "6px 12px",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          currentTheme.colors.hover || "rgba(0,0,0,0.05)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                      title={t("edit")}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onDeleteAllowance)
                          onDeleteAllowance(allowance._id || allowance.id);
                      }}
                      style={{
                        background: "transparent",
                        border: `1px solid ${currentTheme.colors.border}`,
                        cursor: "pointer",
                        color: currentTheme.colors.textSecondary,
                        padding: "6px 12px",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          currentTheme.colors.hover || "rgba(0,0,0,0.05)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                      title={t("delete")}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default AllowancesList;